import { useCallback, useEffect, useState } from "react";
import { Button, Form, Pagination } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";

import {
  EmptyState,
  ErrorAlert,
  Loading,
  PageHeader,
} from "../../layout/secure/common";
import ResultTable from "../../layout/secure/result-table";
import { listResults, messageFromError } from "../../utils/api";

const EMPTY_FILTERS = {
  search: "",
  status: "",
  source: "",
  submittedFrom: "",
  submittedTo: "",
};

function Results() {
  const { surveyId = null } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("lastSavedAt");
  const [direction, setDirection] = useState("desc");

  const load = useCallback(async () => {
    setError("");
    try {
      setData(await listResults({
        ...Object.fromEntries(
          Object.entries(applied).filter(([, value]) => value)
        ),
        page,
        limit: 25,
        sort,
        direction,
      }, surveyId));
    } catch (requestError) {
      setError(messageFromError(requestError, "Survey results could not be loaded."));
    }
  }, [applied, direction, page, sort, surveyId]);

  useEffect(() => { load(); }, [load]);

  const change = event => {
    const { name, value } = event.target;
    setFilters(current => ({
      ...current,
      [name]: value,
      ...(name === "status" && value === "draft"
        ? { submittedFrom: "", submittedTo: "" }
        : {}),
    }));
  };

  const submit = event => {
    event.preventDefault();
    setPage(1);
    setApplied(filters);
  };

  const changeSort = key => {
    if (sort === key) {
      setDirection(value => value === "asc" ? "desc" : "asc");
    } else {
      setSort(key);
      setDirection("asc");
    }
    setPage(1);
  };

  if (!data && !error) return <Loading label="Loading Survey results" />;
  const items = data?.items || [];

  return (
    <>
      <PageHeader
        title={surveyId ? "Survey results" : "Results"}
        description="Filter submitted answers and drafts, then open accessible HTML reports."
        action={surveyId ? (
          <div className="fera-page-buttons">
            <Button as={Link} variant="outline-secondary" to={`/dashboard/surveys/${surveyId}`}>Properties</Button>
            <Button as={Link} to={`/dashboard/surveys/${surveyId}/report`}>Aggregate report</Button>
          </div>
        ) : null}
      />
      <ErrorAlert error={error} onRetry={load} />
      <section className="fera-panel">
        <Form className="fera-result-filters" onSubmit={submit}>
          <Form.Group controlId="result-search">
            <Form.Label>Respondent</Form.Label>
            <Form.Control
              name="search"
              value={filters.search}
              onChange={change}
              placeholder="Name, verified email, or response ID"
            />
          </Form.Group>
          <Form.Group controlId="result-status">
            <Form.Label>Status</Form.Label>
            <Form.Select name="status" value={filters.status} onChange={change}>
              <option value="">All statuses</option>
              <option value="submitted">Submitted</option>
              <option value="draft">Draft</option>
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="result-source">
            <Form.Label>Source</Form.Label>
            <Form.Select name="source" value={filters.source} onChange={change}>
              <option value="">All sources</option>
              <option value="generic-link">Generic link</option>
              <option value="invitation">Invitation</option>
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="result-from">
            <Form.Label>Submitted from</Form.Label>
            <Form.Control type="date" name="submittedFrom" value={filters.submittedFrom} onChange={change} disabled={filters.status === "draft"} />
          </Form.Group>
          <Form.Group controlId="result-to">
            <Form.Label>Submitted to</Form.Label>
            <Form.Control type="date" name="submittedTo" value={filters.submittedTo} onChange={change} disabled={filters.status === "draft"} />
          </Form.Group>
          <div className="fera-filter-actions">
            <Button type="submit">Apply filters</Button>
            <Button
              type="button"
              variant="outline-secondary"
              onClick={() => {
                setFilters(EMPTY_FILTERS);
                setApplied(EMPTY_FILTERS);
                setPage(1);
              }}
            >
              Clear
            </Button>
          </div>
        </Form>
        {!items.length ? (
          <EmptyState
            title="No matching results"
            detail="Submitted responses and authorized drafts will appear here. Try clearing the current filters."
            icon="chart-4"
          />
        ) : (
          <ResultTable
            items={items}
            surveyId={surveyId}
            sort={sort}
            direction={direction}
            onSort={changeSort}
          />
        )}
        {(data?.pagination?.pages || 1) > 1 ? (
          <Pagination className="fera-pagination">
            <Pagination.Prev disabled={page <= 1} onClick={() => setPage(value => Math.max(value - 1, 1))} />
            <Pagination.Item active>{page}</Pagination.Item>
            <Pagination.Next
              disabled={page >= data.pagination.pages}
              onClick={() => setPage(value => value + 1)}
            />
          </Pagination>
        ) : null}
      </section>
    </>
  );
}

export default Results;
